/* Copyright (c) 2020-present CloudIO Inc */

import type {
  ITriggerProps,
  TriggerItemType,
  ItemID,
  Item,
} from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { useEvent, useLatest } from '@cloudio-saas/statex';
import useMounted from 'hooks/useMounted';
import { ABORT_ERROR } from 'platform/AbortError';
import IOError from 'platform/IOError';
import { useState } from 'react';
import { useVisibleChildrenOfType } from 'platform/PlatformHooks';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import {
  useCollectionRecord,
  useEmbeddedContext,
  useRowIndex,
} from 'common/Context';
import { NOT_DEFINED } from 'utils/isEmpty';
import { ActivePubSub } from 'utils/Active';
import {
  processChildTriggers,
  ResolvedFalsePromise,
  ResolvedPromise,
} from './Triggers';
import Logger from 'utils/Logger';
import PubSub from '../utils/PubSub';

export const handleCatchError = (platform: Platform) => (e: IOError) => {
  if (e && (typeof e === 'string' || e.name !== ABORT_ERROR)) {
    if (console.error && typeof e !== 'string') {
      console.error(e);
    }
    platform.showError(typeof e === 'string' ? e : e.message, e.item);
  }
  return ResolvedFalsePromise;
};

const shouldProcessTrigger = (
  props: Omit<ITriggerProps, 'triggerItemId'>,
  trigger: TriggerItemType,
  event?: React.SyntheticEvent<HTMLOrSVGElement>,
) => {
  const { appUid, platform, pageId, itemId } = props;
  if (platform.isDeveloperMode(pageId)) {
    if (
      (
        [
          'TriggerOnExtraLargeScreen',
          'TriggerOnExtraSmallScreen',
          'TriggerOnInitialize',
          'TriggerOnLandscapeScreen',
          'TriggerOnLargeScreen',
          'TriggerOnMediumScreen',
          'TriggerOnPageActive',
          'TriggerOnPageExit',
          'TriggerOnPortraitScreen',
          'TriggerOnSmallScreen',
          'TriggerOnWindowSizeChange',
        ] as TriggerItemType[]
      ).includes(trigger)
    ) {
      // allow these triggers to have the page rendered properly
      // when browser refreshed/resized in designer mode
      return true;
    }
    event?.stopPropagation();
    // @ts-ignore onClick is a dev prop
    if (trigger === 'TriggerOnClick' && props.onClick) {
      // @ts-ignore onClick is a dev prop
      props.onClick(event);
    }
    return false;
  }
  const target = event?.target;
  if (
    (event?.type !== 'keydown' && // ignore if hotkey
      trigger === 'TriggerOnClick' &&
      (target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement)) ||
    (target instanceof HTMLButtonElement && trigger !== 'TriggerOnClick')
  ) {
    event?.stopPropagation();
    return false;
  }
  if (!platform.hasChildrenOfTypeById(appUid, pageId, itemId, trigger)) {
    return false;
  }
  // #3380 We shouldn't call event.stopPropogation here when it is a grid column because it will stop
  // calling onClick Event defined in it's parent (which is Bodycell). As a result mouse down event will be
  // still active and other cells will get selected on cursor move. It is not the case with other column
  // cells. Other cells render their respective CellEditor and starts editing.
  // if (
  //   itemType === 'io.IOGridIconColumn' ||
  //   itemType === 'io.IOGridRowColColumn'
  // ) {
  //   return true;
  // }
  event?.stopPropagation();
  return true;
};

/** Use this hook to bind a child trigger to a component's event
 * @example
 * ```
 * const [onClick, clickInProgress] = useTrigger(
 *  {
 *    platform,
 *    appUid,
 *    roleUid,
 *    pageId,
 *    itemId,
 *  },
 *  'TriggerOnClick',
 * );
 * ```
 * @param props The item context for this trigger
 * @param trigger the trigger type e.g. TriggerOnClick
 * @returns [callback, inProgressStatus]
 */
export default function useTrigger(
  props: Omit<ITriggerProps, 'triggerItemId'>,
  trigger: TriggerItemType,
): [
  (
    | ((
        event?: React.SyntheticEvent<HTMLOrSVGElement>,
        extraProps?: Record<string, unknown>,
        itemId?: ItemID,
      ) => Promise<boolean>)
    | undefined
  ),
  boolean,
  boolean | undefined,
] {
  const [triggerInProgress, setTriggerInProgress] = useState(false);
  const propsRef = useLatest(props);
  const mounted = useMounted();
  const cr = useCollectionRecord();
  const {
    appUid,
    pageId,
    itemId: _itemId,
    disableProgressTracking,
    platform,
  } = props;
  const { children: triggerItemIds } = useVisibleChildrenOfType(
    appUid,
    pageId,
    _itemId,
    trigger,
  );
  let triggerItem;
  if (trigger === 'TriggerOnEnterKeyPress') {
    triggerItem = platform.getItem(
      appUid,
      pageId,
      triggerItemIds[0],
    ) as Item<TriggerItemType>;
  }
  const isTriggerOnTab = triggerItem?.props?.triggerOnTab;
  const store = useCurrentDataStore();
  const _rowIndex = useRowIndex();
  const latestRowIndex = useLatest(_rowIndex);
  const embedCtx = useEmbeddedContext();

  const onTrigger = useEvent(
    async (
      event?: React.SyntheticEvent<HTMLOrSVGElement>,
      extraProps?: Record<string, unknown>,
      itemId?: ItemID,
    ): Promise<boolean> => {
      Logger.trace('Running trigger', trigger, props.itemId);
      ActivePubSub.pub('Activity', { type: 'Trigger' });
      if (event) {
        event.stopPropagation();
      }
      let p = propsRef.current;
      if (itemId) {
        p = { ...propsRef.current, itemId };
      }
      const dsAlias =
        (extraProps?.dsAlias as string) ?? store?.alias ?? NOT_DEFINED;
      const rowIndex =
        (extraProps?.rowIndex as number) ?? latestRowIndex.current;
      if (shouldProcessTrigger(p, trigger, event)) {
        if (!disableProgressTracking) {
          setTriggerInProgress(true);
        }
        return new Promise(async (resolve, reject) => {
          // p.platform.asyncBatchUpdater(async () => {
          const result = await processChildTriggers(
            {
              ...cr,
              ...extraProps,
              ...p,
              dsAlias,
              rowIndex,
              triggerItemId: _itemId,
              embeddedPageInstId: embedCtx.embedded
                ? embedCtx.embeddedPageInstId
                : null,
            },
            trigger,
          )
            .catch(handleCatchError(p.platform))
            .finally(() => {
              if (mounted.current && !disableProgressTracking) {
                setTriggerInProgress(false);
              }
            });
          resolve(result);

          PubSub.pub(
            `TriggerComplete-${appUid}-${pageId}-${_itemId}`,
            {},
            true,
            true,
          );

          Logger.trace('Done running trigger', trigger, props.itemId);
          // });
        });
      }
      return ResolvedPromise;
    },
  );

  return [
    triggerItemIds.length ? onTrigger : undefined,
    triggerInProgress,
    isTriggerOnTab,
  ];
}
